import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Printers.css';
import { useProducts, type PrintersSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const Printers: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products, loading, error } = useProducts<PrintersSpecs>('printers', editMode);
  const navigate = useNavigate();

  const thermalPrinters = products.filter(p => p.specs?.subType == "thermal");
  const impactPrinters  = products.filter(p => p.specs?.subType == "impact");
  const ticketPrinters  = products.filter(p => p.specs?.subType == "tickets");
  const otherPrinters   = products.filter(p => p.specs?.subType == "other");

  const printTypes = [
    {
      title: "Transferencia Térmica",
      description: "Impresión de alta calidad para etiquetas duraderas y códigos de barras precisos.",
    },
    {
      title: "Impacto",
      description: "Impresión robusta para documentos con múltiples copias en ambientes industriales.",
    },
    {
      title: "Tickets",
      description: "Impresión rápida de comprobantes de pesaje y documentación de transacciones.",
    },
    {
      title: "Portátil",
      description: "Soluciones móviles para aplicaciones de campo y trabajo en exterior.",
    },
  ];

  const applications = [
    {
      title: "Pesaje Industrial",
      description: "Comprobantes de peso para básculas de camión e industriales.",
    },
    {
      title: "Logística",
      description: "Etiquetas de envío, tickets de vehículos y control de inventario.",
    },
    {
      title: "Punto de Venta",
      description: "Tickets de venta, etiquetas de precios y comprobantes.",
    },
    {
      title: "Manufactura",
      description: "Etiquetas de productos, control de calidad y trazabilidad.",
    },
    {
      title: "Transporte",
      description: "Documentación de carga, manifiestos y controles de acceso.",
    },
    {
      title: "Administración",
      description: "Reportes, formularios y documentación administrativa.",
    },
  ];

  const overviewFeatures = [
    "Impresoras térmicas",
    "Impresoras de impacto",
    "Impresoras de tickets",
    "Memoria fecha/hora",
    "Impresión de logos",
  ];

  return (
    <div className="pr-page">
      {error && <div className="pr-status-bar pr-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="pr-hero">
        <div className="container pr-hero-content">
          <nav className="pr-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="pr-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="pr-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Impresoras</span>
          </nav>
          <h1 className="pr-hero-title">Impresoras</h1>
          <p className="pr-hero-subtitle">
            Equipos de impresión especializados para etiquetas, tickets y
            comprobantes de pesaje en aplicaciones industriales.
          </p>
          {authType === 'internal' && (
            <button
              className={`pr-btn-edit-mode${editMode ? ' pr-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="pr-hero-image" aria-hidden="true">
          <img src="/images/printers.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="pr-overview">
        <div className="container">
          <div className="pr-overview-grid">
            <div className="pr-overview-text">
              <span className="pr-label">Descripción general</span>
              <h2>Soluciones completas de impresión industrial</h2>
              <p>
                Ofrecemos una amplia gama de impresoras especializadas para aplicaciones
                industriales, incluyendo impresoras térmicas para etiquetas, impresoras
                de impacto para documentación robusta, e impresoras de tickets para
                básculas y sistemas de pesaje.
              </p>
            </div>
            <div className="pr-overview-image">
              <ul className="pr-features-list" aria-label="Características principales">
            {overviewFeatures.map((feature, index) => (
              <li key={index} className="pr-feature-item">
                <svg className="pr-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {feature}
              </li>
            ))}
          </ul>
            </div>
          </div>
          
        </div>
      </section>

      {/* ── Thermal Printers ─────────────────────────────────── */}
      <section className="pr-products pr-products--alt">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Impresoras Térmicas</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/printers')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : thermalPrinters.map((printer, index) => (
                <ProductCard
                  key={index}
                  reviewed={printer.reviewed}
                  endOfLife={printer.endOfLife}
                  productId={printer.productId}
                  name={printer.name}
                  badge={printer.specs?.model ?? undefined}
                  sub={printer.specs?.type ?? undefined}
                  description={printer.specs?.description ?? undefined}
                  image={printer.image}
                  onRevisar={editMode && printer.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(printer.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (printer.specs?.connectivity  ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.paperTypes?.length   ? [{ icon: '📄', label: 'Tipos de papel',  items: (printer.specs?.paperTypes    ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (printer.specs?.features      ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones',    items: (printer.specs?.applications  ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Impact Printers ──────────────────────────────────── */}
      <section className="pr-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Impresoras de Impacto</h2>
            </div>
            <div className="models-grid">
              {impactPrinters.map((printer, index) => (
                <ProductCard
                  key={index}
                  reviewed={printer.reviewed}
                  endOfLife={printer.endOfLife}
                  productId={printer.productId}
                  name={printer.name}
                  badge={printer.specs?.model ?? undefined}
                  sub={printer.specs?.type ?? undefined}
                  description={printer.specs?.description ?? undefined}
                  image={printer.image}
                  onRevisar={editMode && printer.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(printer.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (printer.specs?.connectivity  ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.paperTypes?.length   ? [{ icon: '📄', label: 'Tipos de papel',  items: (printer.specs?.paperTypes    ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (printer.specs?.features      ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones',    items: (printer.specs?.applications  ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Ticket Printers ──────────────────────────────────── */}
      <section className="pr-products pr-products--alt">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Impresoras de Tickets</h2>
            </div>
            <div className="models-grid">
              {ticketPrinters.map((printer, index) => (
                <ProductCard
                  key={index}
                  reviewed={printer.reviewed}
                  endOfLife={printer.endOfLife}
                  productId={printer.productId}
                  name={printer.name}
                  badge={printer.specs?.model ?? undefined}
                  sub={printer.specs?.type ?? undefined}
                  description={printer.specs?.description ?? undefined}
                  image={printer.image}
                  onRevisar={editMode && printer.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(printer.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (printer.specs?.connectivity  ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.paperTypes?.length   ? [{ icon: '📄', label: 'Tipos de papel',  items: (printer.specs?.paperTypes    ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (printer.specs?.features      ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones',    items: (printer.specs?.applications  ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Other Printers ───────────────────────────────────── */}
      <section className="pr-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Otras Impresoras</h2>
            </div>
            <div className="models-grid">
              {otherPrinters.map((printer, index) => (
                <ProductCard
                  key={index}
                  reviewed={printer.reviewed}
                  endOfLife={printer.endOfLife}
                  productId={printer.productId}
                  name={printer.name}
                  badge={printer.specs?.model ?? undefined}
                  sub={printer.specs?.type ?? undefined}
                  description={printer.specs?.description ?? undefined}
                  image={printer.image}
                  onRevisar={editMode && printer.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(printer.specs?.connectivity?.length ? [{ icon: '🔌', label: 'Conectividad',    items: (printer.specs?.connectivity  ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.paperTypes?.length   ? [{ icon: '📄', label: 'Tipos de papel',  items: (printer.specs?.paperTypes    ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.features?.length     ? [{ icon: '⚙️', label: 'Características', items: (printer.specs?.features      ?? []).filter((x): x is string => x != null) }] : []),
                    ...(printer.specs?.applications?.length ? [{ icon: '🏭', label: 'Aplicaciones',    items: (printer.specs?.applications  ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Print types ──────────────────────────────────────── */}
      <section className="pr-types">
        <div className="container">
          <span className="pr-label">Tecnologías</span>
          <h2 className="pr-section-title">Tipos de Impresión</h2>
          <div className="pr-types-grid">
            {printTypes.map((type, index) => (
              <div key={index} className="pr-type-card">
                <h3>{type.title}</h3>
                <p>{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="pr-applications">
        <div className="container">
          <span className="pr-label pr-label--light">Industrias</span>
          <h2 className="pr-section-title pr-section-title--light">Aplicaciones Industriales</h2>
          <div className="pr-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="pr-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Printers;
